import Button, { type ButtonProps } from '@mui/material/Button';
import type { Theme } from '@mui/material/styles';
import { motion, tokens } from '@theme/tokens';
import { forwardRef } from 'react';

/** Resting key: inner top highlight plus the rail's soft teal glow. */
const REST = 'inset 0 1px 0 rgba(255,255,255,0.22), 0 2px 12px -2px rgba(126,186,186,0.5)';
const HOVER = 'inset 0 1px 0 rgba(255,255,255,0.28), 0 4px 16px -2px rgba(126,186,186,0.65)';
/** Pressed: the glow collapses and the key sinks into its bed. */
const PRESSED = 'inset 0 2px 4px rgba(13,34,34,0.30), 0 1px 4px -1px rgba(126,186,186,0.4)';

const transition = ['box-shadow', 'transform', 'background-color', 'color']
  .map((p) => `${p} ${String(motion.durationMs.standard)}ms ease-out`)
  .join(', ');

function clayKeySx(theme: Theme) {
  return {
    '&&': {
      minHeight: 44,
      px: 2.5,
      borderRadius: `${String(tokens.radius.pill)}px`,
      color: '#ffffff',
      background: tokens.gradient.navPill,
      boxShadow: REST,
      transition,
      '&:hover': {
        background: tokens.gradient.navPill,
        boxShadow: HOVER,
        transform: 'translateY(-1px)',
      },
      '&:active': {
        boxShadow: PRESSED,
        transform: 'translateY(0) scale(0.98)',
      },
      '&.Mui-focusVisible': {
        outline: 'none',
        ...theme.applyStyles('light', { boxShadow: `${tokens.focusRing.light}, ${REST}` }),
        ...theme.applyStyles('dark', { boxShadow: `${tokens.focusRing.dark}, ${REST}` }),
      },
      // A key that can't be pressed sinks into the surface instead of greying out on top of it.
      '&.Mui-disabled': {
        color: 'text.disabled',
        background: tokens.color.surface.well,
        boxShadow: tokens.shadow.insetWell,
        transform: 'none',
        ...theme.applyStyles('dark', {
          background: tokens.color.surface.darkWell,
          boxShadow: tokens.shadow.darkInsetWell,
        }),
      },
      '@media (prefers-reduced-motion: reduce)': {
        '&:hover, &:active': { transform: 'none' },
      },
    },
  };
}

/** Look is fixed on purpose: every primary key reads the same, so no variant/colour/sx here. */
export type ClayButtonProps = Omit<ButtonProps, 'variant' | 'color' | 'sx'>;

/**
 * The machined primary key of the instrument rail, as a button: a navPill-gradient pill with
 * an inner highlight and a soft teal glow that lifts on hover and gives under the finger.
 * One per view, for the single primary action; secondary actions stay outlined "physical keys".
 */
export const ClayButton = forwardRef<HTMLButtonElement, ClayButtonProps>(
  function ClayButton(props, ref) {
    return (
      <Button
        ref={ref}
        size="large"
        disableElevation
        {...props}
        // `inherit` keeps the theme's anodized containedPrimary recipe out of the way.
        variant="contained"
        color="inherit"
        sx={clayKeySx}
      />
    );
  },
);
